import type { Locale } from './types';

export type UiDictionary = {
  hero: {
    eyebrow: string;
    title: string;
    tagline: string;
    overallProgress: string;
    lightMode: string;
    darkMode: string;
  };
  stageNav: {
    ariaLabel: string;
  };
  stageView: {
    mindCardsTitle: string;
    tipsTitle: string;
    checklistTitle: string;
    adminGuideTitle: string;
    shoppingTitle: string;
    careScenesTitle: string;
    warningsTitle: string;
    partnerTitle: string;
    exercisesTitle: string;
  };
  checklist: {
    progress: string;
    completed: string;
    completedCount: string;
    celebration: string;
    filterAriaLabel: string;
    all: string;
    empty: string;
    resetStage: string;
    resetAll: string;
    resetConfirm: (stageTitle: string) => string;
    categoryLabels: {
      body: string;
      mind: string;
      hospital: string;
      admin: string;
      shopping: string;
      life: string;
      partner: string;
    };
    editMode: string;
    doneEdit: string;
    addItem: string;
    addPlaceholder: string;
    addButton: string;
    editItem: string;
    deleteItem: string;
    deleteConfirm: string;
    restoreDefaults: string;
    restoreConfirm: string;
    customBadge: string;
    categoryLabel: string;
  };
  shopping: {
    note: string;
    timing: string;
    free: string;
    priorityLabels: {
      must: string;
      should: string;
      nice: string;
    };
    editMode: string;
    doneEdit: string;
    addItem: string;
    addPlaceholder: string;
    addButton: string;
    editItem: string;
    deleteItem: string;
    deleteConfirm: string;
    customBadge: string;
  };
  mindCards: {
    typeLabels: {
      mind: string;
      body: string;
      life: string;
      partner: string;
    };
  };
  disclaimer: {
    title: string;
    body: string;
    copy: string;
  };
  warningSigns: {
    intro: string;
    urgentTitle: string;
    watchTitle: string;
  };
  languageSwitcher: {
    label: string;
    ariaLabel: string;
  };
  expand: {
    more: string;
    less: string;
    detail: string;
    hideDetail: string;
    moreItems: string;
    lessItems: string;
  };
  install: {
    title: string;
    body: string;
    dismiss: string;
    iosStep1: string;
    iosStep2: string;
    iosStep3: string;
    androidButton: string;
    androidHint: string;
    note: string;
  };
  reminder: {
    title: string;
    body: string;
    hourLabel: string;
    hourOption: string;
    enable: string;
    disable: string;
    test: string;
    testTitle: string;
    testBody: string;
    testOk: string;
    enabledOk: string;
    disabledOk: string;
    permissionDenied: string;
    syncFailed: string;
    unsupported: string;
    iosNote: string;
  };
  onboarding: {
    eyebrow: string;
    title: string;
    body: string;
    languageLabel: string;
    nameLabel: string;
    namePlaceholder: string;
    pregnantTab: string;
    postpartumTab: string;
    dueDateLabel: string;
    birthDateLabel: string;
    save: string;
    skip: string;
    restoreTitle: string;
    restoreNote: string;
    restoreButton: string;
    restoreOk: string;
    restoreFailed: string;
  };
  settings: {
    title: string;
    clear: string;
    close: string;
    accountsLabel: string;
    addAccount: string;
    deleteAccount: string;
    deleteConfirm: string;
    cloudTitle: string;
    cloudNote: string;
    cloudSave: string;
    cloudRestore: string;
    cloudSaved: string;
    cloudSaveFailed: string;
  };
  focus: {
    title: string;
    noProfile: string;
    setDates: string;
    editDates: string;
    allDone: string;
  };
  weekCard: {
    title: string;
    sizeLabel: string;
    fruitLike: string;
    disclaimer: string;
    postpartumTitle: string;
    postpartumTip: string;
  };
  search: {
    ariaLabel: string;
    placeholder: string;
    empty: string;
    done: string;
  };
  share: {
    title: string;
    intro: string;
    forMomTitle: string;
    forMomBody: string;
    createCode: string;
    updateCode: string;
    codeLabel: string;
    copyCode: string;
    codeCopied: string;
    sendHint: string;
    forFamilyTitle: string;
    forFamilyBody: string;
    codePlaceholder: string;
    syncButton: string;
    syncOk: string;
    syncFailed: string;
    copyText: string;
    copiedText: string;
    copyFailed: string;
    textHint: string;
    autoOn: string;
    autoHint: string;
    unlink: string;
    unlinked: string;
    lastSync: string;
  };
};

const ja: UiDictionary = {
  hero: {
    eyebrow: '新潟市の妊娠家庭向けガイド',
    title: 'よりそいノート',
    tagline: '妊娠から産後まで、いまの自分に必要な準備をひとつずつ。家族とも進み具合を共有できます。',
    overallProgress: '全体の進捗',
    lightMode: 'ライトモードに切り替え',
    darkMode: 'ダークモードに切り替え',
  },
  stageNav: {
    ariaLabel: '時期ナビゲーション',
  },
  stageView: {
    mindCardsTitle: 'いま大切にしたいこと',
    tipsTitle: '先輩ママのヒント',
    checklistTitle: 'チェックリスト',
    adminGuideTitle: '手続き・お金',
    shoppingTitle: '用意するもの',
    careScenesTitle: 'シーン別ケア',
    warningsTitle: '気をつけたいサイン',
    partnerTitle: 'パートナーへ',
    exercisesTitle: 'エクササイズ',
  },
  checklist: {
    progress: '進捗',
    completed: '完了',
    completedCount: '{done}/{total} 完了',
    celebration: 'この時期のチェック完了！すてきな準備ができました。',
    filterAriaLabel: 'カテゴリ絞り込み',
    all: 'すべて',
    empty: 'このカテゴリの項目はありません',
    resetStage: 'この時期をリセット',
    resetAll: 'すべてリセット',
    resetConfirm: (stageTitle) => `${stageTitle}のチェックをリセットしますか？`,
    categoryLabels: {
      body: 'からだ',
      mind: 'こころ',
      hospital: '病院・健診',
      admin: '手続き',
      shopping: '買い物',
      life: '暮らし',
      partner: 'パートナー',
    },
    editMode: '項目を編集',
    doneEdit: '編集を終える',
    addItem: '項目を追加',
    addPlaceholder: '例: 実家への連絡',
    addButton: '追加する',
    editItem: '名前を変える',
    deleteItem: '削除',
    deleteConfirm: 'この項目をリストから外しますか？',
    restoreDefaults: 'この時期の標準に戻す',
    restoreConfirm: 'この時期の追加・削除・名前変更をすべて取り消しますか？',
    customBadge: '追加分',
    categoryLabel: 'カテゴリ',
  },
  shopping: {
    note: '価格は目安です。店舗・時期・ブランドにより変動します。自分用に増やしたり、要らないものを消せます。',
    timing: '時期:',
    free: '無料',
    priorityLabels: {
      must: '必須',
      should: '推奨',
      nice: 'あれば便利',
    },
    editMode: '買い物を編集',
    doneEdit: '編集を終える',
    addItem: '買い物を追加',
    addPlaceholder: '例: 授乳クッション',
    addButton: '追加する',
    editItem: '名前を変える',
    deleteItem: '削除',
    deleteConfirm: 'この買い物をリストから外しますか？',
    customBadge: '追加分',
  },
  mindCards: {
    typeLabels: {
      mind: 'こころ',
      body: 'からだ',
      life: '暮らし',
      partner: 'パートナー',
    },
  },
  disclaimer: {
    title: '免責事項：',
    body: '本サイトは妊娠・産後に関する一般的な情報をまとめたものです。医療診断や個別の治療・アドバイスを行うものではありません。気になる症状や不安がある場合は、かかりつけの産婦人科・医療機関、または自治体・保健所の相談窓口へご相談ください。',
    copy: '© よりそいノート',
  },
  warningSigns: {
    intro: '気になる症状は、かかりつけの医療機関や自治体の相談窓口へ。',
    urgentTitle: 'すぐに相談したい',
    watchTitle: '様子を見ながら相談したい',
  },
  languageSwitcher: {
    label: 'Language',
    ariaLabel: '表示言語を切り替える',
  },
  expand: {
    more: 'もっと見る',
    less: '閉じる',
    detail: '詳しく',
    hideDetail: '詳細を閉じる',
    moreItems: 'ほか {count} 件をもっと見る',
    lessItems: '少なく表示',
  },
  install: {
    title: 'ホーム画面に追加',
    body: 'アプリストアは不要です。スマホのホーム画面に置くと、いつでもアプリのように開けます。',
    dismiss: '閉じる',
    iosStep1: 'Safari の共有ボタン（四角と矢印）をタップ',
    iosStep2: '「ホーム画面に追加」を選ぶ',
    iosStep3: '「追加」を押す',
    androidButton: 'アプリとして追加',
    androidHint: 'ブラウザのメニューから「ホーム画面に追加」または「アプリをインストール」を選んでください。',
    note: '通知を使う場合は、ホーム画面から開いた状態で許可すると確実です。',
  },
  reminder: {
    title: 'リマインド通知',
    body: '決めた時間に「今日の準備、終わっていますか？」とスマホへお知らせします。',
    hourLabel: '通知したい時刻（日本時間）',
    hourOption: '{hour}:00',
    enable: '通知をオン',
    disable: '通知をオフ',
    test: 'テスト通知',
    testTitle: 'よりそいノート',
    testBody: 'テストです。今日の準備、終わっていますか？',
    testOk: 'テスト通知を送りました',
    enabledOk: 'リマインドをオンにしました',
    disabledOk: 'リマインドをオフにしました',
    permissionDenied: '通知が許可されていません。端末の設定で許可してください。',
    syncFailed: '通知の登録に失敗しました。通信環境を確認してもう一度。',
    unsupported: 'このブラウザでは通知に対応していません。',
    iosNote: 'iPhone では、先にホーム画面へ追加してから通知をオンにしてください。',
  },
  onboarding: {
    eyebrow: 'はじめの設定',
    title: '言語と予定日を設定しましょう',
    body: 'この端末にあなたの状況を保存します。家族で使う場合は、あとからユーザーを追加できます。クラウド保存コードも使えます。',
    languageLabel: '表示言語',
    nameLabel: 'よび名',
    namePlaceholder: '例: わたし / ママ / パパ',
    pregnantTab: '妊娠中',
    postpartumTab: '産後',
    dueDateLabel: '出産予定日',
    birthDateLabel: '赤ちゃんの誕生日',
    save: '保存してはじめる',
    skip: 'あとで設定する',
    restoreTitle: '保存コードから復元',
    restoreNote: '別の端末で発行したコードがあれば、ここで状況を取り戻せます。',
    restoreButton: '復元する',
    restoreOk: 'データを復元しました',
    restoreFailed: 'コードを確認してください',
  },
  settings: {
    title: 'ユーザーと日付の設定',
    clear: '日付を消す',
    close: '閉じる',
    accountsLabel: 'ユーザー',
    addAccount: 'ユーザーを追加',
    deleteAccount: 'このユーザーを削除',
    deleteConfirm: '「{name}」を削除しますか？チェック履歴も消えます。',
    cloudTitle: 'クラウドに保存',
    cloudNote: '復旧コードを発行すると、別の端末でも同じ状況を復元できます。コードは大切に控えてください。',
    cloudSave: '保存コードを発行',
    cloudRestore: 'コードで復元',
    cloudSaved: '保存コード: {code}',
    cloudSaveFailed: 'クラウド保存に失敗しました',
  },
  focus: {
    title: '今週のフォーカス',
    noProfile: '予定日を設定すると、いまの時期に合わせて案内します',
    setDates: '予定日を設定',
    editDates: '日付を変更',
    allDone: 'この時期のチェックはすべて完了です。お疲れさまです。',
  },
  weekCard: {
    title: '妊娠{week}週{days}日',
    sizeLabel: '大きさの目安',
    fruitLike: '{fruit}くらい',
    disclaimer: '大きさは一般的な目安です。個人差があります。',
    postpartumTitle: '産後のいま',
    postpartumTip: '回復と育児の両立は人それぞれ。眠れたら寝る、頼れたら頼る、で大丈夫です。',
  },
  search: {
    ariaLabel: 'チェック項目の検索',
    placeholder: '手続きや買い物を検索',
    empty: '該当する項目はありません',
    done: '完了済み',
  },
  share: {
    title: '家族と進行を共有',
    intro:
      '新潟市で暮らす妊娠家庭向けです。ママが進行状況の共有コードを作り、夫・家族・友人に送ると、同じチェックが見られます。',
    forMomTitle: '① ママ・本人：共有コードを作る',
    forMomBody: 'いまのチェックと予定日をクラウドに保存し、短いコードを発行します。同じコードで何度でも上書き更新できます。',
    createCode: '共有コードを発行',
    updateCode: '同じコードで最新に更新',
    codeLabel: '共有コード',
    copyCode: 'コードをコピー',
    codeCopied: 'コードをコピーしました。LINEなどで送ってください。',
    sendHint: 'LINEやメッセージでこのコードを家族に送ってください。',
    forFamilyTitle: '② 夫・家族・友人：コードで同期',
    forFamilyBody: '受け取ったコードを入れて「同期する」と、チェック状態と予定日がこの端末に取り込まれます。',
    codePlaceholder: '例: ABCD-EFGH',
    syncButton: '同期する',
    syncOk: '家族の進行状況を同期しました',
    syncFailed: 'コードを確認してもう一度試してください',
    copyText: 'いまの進捗をテキストでコピー',
    copiedText: 'テキストをコピーしました',
    copyFailed: 'コピーに失敗しました。手動で選択してください。',
    textHint: 'コードのほか、進捗の要約テキストだけ送りたいときにも使えます。',
    autoOn: '自動同期オン',
    autoHint:
      '一度つなぐと、アプリを開いたとき・チェックを変えたとき・約3分ごとに自動で同期します。毎回コードを発行し直す必要はありません。',
    unlink: '自動同期を解除',
    unlinked: '自動同期を解除しました',
    lastSync: '最終同期: {time}',
  },
};

const en: UiDictionary = {
  hero: {
    eyebrow: 'For families in Niigata City',
    title: 'Yorisoi Note',
    tagline: 'Step by step preparation from pregnancy to postpartum. Share progress with your family.',
    overallProgress: 'Overall Progress',
    lightMode: 'Switch to light mode',
    darkMode: 'Switch to dark mode',
  },
  stageNav: {
    ariaLabel: 'Stage navigation',
  },
  stageView: {
    mindCardsTitle: 'What matters now',
    tipsTitle: 'Tips from experienced moms',
    checklistTitle: 'Checklist',
    adminGuideTitle: 'Paperwork & money',
    shoppingTitle: 'Things to prepare',
    careScenesTitle: 'Care by scene',
    warningsTitle: 'Warning signs',
    partnerTitle: 'For partners',
    exercisesTitle: 'Exercise',
  },
  checklist: {
    progress: 'Progress',
    completed: 'done',
    completedCount: '{done}/{total} done',
    celebration: 'Checklist complete for this stage! Great preparation.',
    filterAriaLabel: 'Filter by category',
    all: 'All',
    empty: 'No items in this category',
    resetStage: 'Reset this stage',
    resetAll: 'Reset all',
    resetConfirm: (stageTitle) => `Reset checklist for ${stageTitle}?`,
    categoryLabels: {
      body: 'Body',
      mind: 'Mind',
      hospital: 'Hospital',
      admin: 'Paperwork',
      shopping: 'Shopping',
      life: 'Daily life',
      partner: 'Partner',
    },
    editMode: 'Edit items',
    doneEdit: 'Done editing',
    addItem: 'Add item',
    addPlaceholder: 'e.g. Call parents',
    addButton: 'Add',
    editItem: 'Rename',
    deleteItem: 'Remove',
    deleteConfirm: 'Remove this item from your list?',
    restoreDefaults: 'Restore stage defaults',
    restoreConfirm: 'Undo all add/remove/rename changes for this stage?',
    customBadge: 'Custom',
    categoryLabel: 'Category',
  },
  shopping: {
    note: 'Prices are approximate. You can add what you need and remove what you do not.',
    timing: 'Timing:',
    free: 'Free',
    priorityLabels: {
      must: 'Must',
      should: 'Should',
      nice: 'Nice to have',
    },
    editMode: 'Edit shopping',
    doneEdit: 'Done editing',
    addItem: 'Add item',
    addPlaceholder: 'e.g. Nursing pillow',
    addButton: 'Add',
    editItem: 'Rename',
    deleteItem: 'Remove',
    deleteConfirm: 'Remove this shopping item?',
    customBadge: 'Custom',
  },
  mindCards: {
    typeLabels: {
      mind: 'Mind',
      body: 'Body',
      life: 'Life',
      partner: 'Partner',
    },
  },
  disclaimer: {
    title: 'Disclaimer: ',
    body: 'This site provides general information about pregnancy and postpartum. It is not a substitute for medical diagnosis or individualized treatment and advice. If you have symptoms or concerns, please consult your obstetrician/gynecologist, medical institution, or local government/health center consultation desk.',
    copy: '© Yorisoi Note',
  },
  warningSigns: {
    intro: 'If you have concerning symptoms, consult your healthcare provider or local government consultation desk.',
    urgentTitle: 'Consult immediately',
    watchTitle: 'Monitor and consult',
  },
  languageSwitcher: {
    label: 'Language',
    ariaLabel: 'Switch display language',
  },
  expand: {
    more: 'Show more',
    less: 'Show less',
    detail: 'Details',
    hideDetail: 'Hide details',
    moreItems: 'Show {count} more',
    lessItems: 'Show fewer',
  },
  install: {
    title: 'Add to Home Screen',
    body: 'No App Store needed. Add this site to your phone home screen and open it like an app anytime.',
    dismiss: 'Dismiss',
    iosStep1: 'Tap the Share button in Safari (square with arrow)',
    iosStep2: 'Choose "Add to Home Screen"',
    iosStep3: 'Tap "Add"',
    androidButton: 'Install app',
    androidHint: 'Use the browser menu: "Add to Home screen" or "Install app".',
    note: 'For notifications, open the app from the home screen and allow permission there.',
  },
  reminder: {
    title: 'Reminder notifications',
    body: 'At your chosen time, your phone asks: "Any checklist items left for today?"',
    hourLabel: 'Reminder time (Japan time)',
    hourOption: '{hour}:00',
    enable: 'Turn reminders on',
    disable: 'Turn reminders off',
    test: 'Send test',
    testTitle: 'Yorisoi Note',
    testBody: 'Test message. Any checklist items left for today?',
    testOk: 'Test notification sent',
    enabledOk: 'Reminders turned on',
    disabledOk: 'Reminders turned off',
    permissionDenied: 'Notifications are blocked. Allow them in device settings.',
    syncFailed: 'Could not register notifications. Check your connection and try again.',
    unsupported: 'Notifications are not supported in this browser.',
    iosNote: 'On iPhone, add to Home Screen first, then turn notifications on.',
  },
  onboarding: {
    eyebrow: 'Quick setup',
    title: 'Choose language and your dates',
    body: 'Your situation is saved on this device. You can add family users later, or restore with a cloud save code.',
    languageLabel: 'Language',
    nameLabel: 'Display name',
    namePlaceholder: 'e.g. Me / Mom / Partner',
    pregnantTab: 'Pregnant',
    postpartumTab: 'Postpartum',
    dueDateLabel: 'Due date',
    birthDateLabel: 'Baby birthday',
    save: 'Save and start',
    skip: 'Set up later',
    restoreTitle: 'Restore with a save code',
    restoreNote: 'If you have a code from another device, restore your data here.',
    restoreButton: 'Restore',
    restoreOk: 'Data restored',
    restoreFailed: 'Check the code and try again',
  },
  settings: {
    title: 'Users and dates',
    clear: 'Clear dates',
    close: 'Close',
    accountsLabel: 'Users',
    addAccount: 'Add user',
    deleteAccount: 'Delete this user',
    deleteConfirm: 'Delete "{name}"? Checklist history will be removed.',
    cloudTitle: 'Cloud save',
    cloudNote: 'Create a recovery code to restore the same situation on another device. Keep the code safe.',
    cloudSave: 'Create save code',
    cloudRestore: 'Restore with code',
    cloudSaved: 'Save code: {code}',
    cloudSaveFailed: 'Cloud save failed',
  },
  focus: {
    title: 'This week focus',
    noProfile: 'Set a due date to personalize this stage',
    setDates: 'Set dates',
    editDates: 'Edit dates',
    allDone: 'All checklist items for this stage are done. Nice work.',
  },
  weekCard: {
    title: 'Week {week}+{days}',
    sizeLabel: 'Size guide',
    fruitLike: 'About a {fruit}',
    disclaimer: 'Sizes are general guides and vary by individual.',
    postpartumTitle: 'Postpartum now',
    postpartumTip: 'Recovery and caregiving look different for everyone. Rest when you can, ask for help when you can.',
  },
  search: {
    ariaLabel: 'Search checklist items',
    placeholder: 'Search paperwork or shopping',
    empty: 'No matching items',
    done: 'Done',
  },
  share: {
    title: 'Share progress with family',
    intro:
      'Made for pregnancy families in Niigata City. Mom creates a share code; partner, family, or friends can sync the same checklist.',
    forMomTitle: '① Mom: create a share code',
    forMomBody: 'Save the current checklist and due date to the cloud and get a short code. You can update the same code anytime.',
    createCode: 'Create share code',
    updateCode: 'Update this code with latest progress',
    codeLabel: 'Share code',
    copyCode: 'Copy code',
    codeCopied: 'Code copied. Send it via LINE or message.',
    sendHint: 'Send this code to your family.',
    forFamilyTitle: '② Partner / family: sync with the code',
    forFamilyBody: 'Enter the code and tap Sync to import checklist progress and due date onto this device.',
    codePlaceholder: 'e.g. ABCD-EFGH',
    syncButton: 'Sync',
    syncOk: 'Family progress synced',
    syncFailed: 'Check the code and try again',
    copyText: 'Copy progress as text',
    copiedText: 'Text copied',
    copyFailed: 'Copy failed. Please select manually.',
    textHint: 'Use this when you only want to send a text summary.',
    autoOn: 'Auto-sync on',
    autoHint:
      'After linking once, the app syncs when opened, when checklist items change, and about every 3 minutes. You do not need a new code each time.',
    unlink: 'Turn off auto-sync',
    unlinked: 'Auto-sync turned off',
    lastSync: 'Last sync: {time}',
  },
};

const zh: UiDictionary = {
  hero: {
    eyebrow: '面向新潟市孕产家庭',
    title: '依偎笔记',
    tagline: '从怀孕到产后，一步一步做好当下需要的准备。也可与家人同步进度。',
    overallProgress: '整体进度',
    lightMode: '切换到浅色模式',
    darkMode: '切换到深色模式',
  },
  stageNav: {
    ariaLabel: '阶段导航',
  },
  stageView: {
    mindCardsTitle: '此刻最重要的事',
    tipsTitle: '妈妈们的经验分享',
    checklistTitle: '清单',
    adminGuideTitle: '手续与费用',
    shoppingTitle: '需要准备的东西',
    careScenesTitle: '按场景护理',
    warningsTitle: '需要注意的信号',
    partnerTitle: '给伴侣',
    exercisesTitle: '运动',
  },
  checklist: {
    progress: '进度',
    completed: '已完成',
    completedCount: '{done}/{total} 已完成',
    celebration: '本阶段清单完成！准备得很棒。',
    filterAriaLabel: '按类别筛选',
    all: '全部',
    empty: '该类别没有项目',
    resetStage: '重置本阶段',
    resetAll: '重置全部',
    resetConfirm: (stageTitle) => `要重置「${stageTitle}」的清单吗？`,
    categoryLabels: {
      body: '身体',
      mind: '心理',
      hospital: '医院・产检',
      admin: '手续',
      shopping: '购物',
      life: '生活',
      partner: '伴侣',
    },
    editMode: '编辑项目',
    doneEdit: '完成编辑',
    addItem: '添加项目',
    addPlaceholder: '例如：联系父母',
    addButton: '添加',
    editItem: '改名',
    deleteItem: '删除',
    deleteConfirm: '要从列表中移除该项吗？',
    restoreDefaults: '恢复本阶段默认',
    restoreConfirm: '撤销本阶段的全部增删改名吗？',
    customBadge: '自加',
    categoryLabel: '类别',
  },
  shopping: {
    note: '价格为参考。可以增加需要的、删除不需要的。',
    timing: '时期:',
    free: '免费',
    priorityLabels: {
      must: '必需',
      should: '推荐',
      nice: '有更好',
    },
    editMode: '编辑购物',
    doneEdit: '完成编辑',
    addItem: '添加购物',
    addPlaceholder: '例如：哺乳枕',
    addButton: '添加',
    editItem: '改名',
    deleteItem: '删除',
    deleteConfirm: '要从列表中移除该购物项吗？',
    customBadge: '自加',
  },
  mindCards: {
    typeLabels: {
      mind: '心理',
      body: '身体',
      life: '生活',
      partner: '伴侣',
    },
  },
  disclaimer: {
    title: '免责声明：',
    body: '本网站汇总了关于怀孕、产后的一般信息，不构成医疗诊断或个别治疗、建议。如有担心的症状或不安，请咨询常去的妇产科、医疗机构，或地方政府、保健所的咨询窗口。',
    copy: '© 依偎笔记',
  },
  warningSigns: {
    intro: '如有担心的症状，请咨询常去的医疗机构或地方政府咨询窗口。',
    urgentTitle: '请立即咨询',
    watchTitle: '观察并咨询',
  },
  languageSwitcher: {
    label: 'Language',
    ariaLabel: '切换显示语言',
  },
  expand: {
    more: '查看更多',
    less: '收起',
    detail: '详细',
    hideDetail: '收起详细',
    moreItems: '再看 {count} 项',
    lessItems: '收起列表',
  },
  install: {
    title: '添加到主屏幕',
    body: '不需要应用商店。把它放到手机主屏幕后，就能像 App 一样随时打开。',
    dismiss: '关闭',
    iosStep1: '在 Safari 点击分享按钮（方框加箭头）',
    iosStep2: '选择「添加到主屏幕」',
    iosStep3: '点击「添加」',
    androidButton: '安装到主屏幕',
    androidHint: '请在浏览器菜单中选择「添加到主屏幕」或「安装应用」。',
    note: '若要使用通知，请从主屏幕打开后再允许通知权限。',
  },
  reminder: {
    title: '提醒通知',
    body: '在设定的时间，手机会问你：「今天的准备事项做完了吗？」',
    hourLabel: '通知时间（日本时间）',
    hourOption: '{hour}:00',
    enable: '开启通知',
    disable: '关闭通知',
    test: '发送测试',
    testTitle: '依偎笔记',
    testBody: '这是测试。今天的准备事项做完了吗？',
    testOk: '已发送测试通知',
    enabledOk: '已开启提醒',
    disabledOk: '已关闭提醒',
    permissionDenied: '通知未被允许。请在设备设置中开启。',
    syncFailed: '通知注册失败。请检查网络后重试。',
    unsupported: '当前浏览器不支持通知。',
    iosNote: 'iPhone 请先添加到主屏幕，再开启通知。',
  },
  onboarding: {
    eyebrow: '初次设置',
    title: '选择语言并填写日期',
    body: '会把你的情况保存在本设备。之后也可以为家人添加用户，或用云端保存码恢复。',
    languageLabel: '显示语言',
    nameLabel: '称呼',
    namePlaceholder: '例如：我 / 妈妈 / 爸爸',
    pregnantTab: '孕期',
    postpartumTab: '产后',
    dueDateLabel: '预产期',
    birthDateLabel: '宝宝生日',
    save: '保存并开始',
    skip: '稍后设置',
    restoreTitle: '用保存码恢复',
    restoreNote: '如果有在其他设备生成的保存码，可在这里恢复数据。',
    restoreButton: '恢复',
    restoreOk: '已恢复数据',
    restoreFailed: '请检查保存码',
  },
  settings: {
    title: '用户与日期设置',
    clear: '清除日期',
    close: '关闭',
    accountsLabel: '用户',
    addAccount: '添加用户',
    deleteAccount: '删除此用户',
    deleteConfirm: '要删除「{name}」吗？清单记录也会消失。',
    cloudTitle: '云端保存',
    cloudNote: '生成恢复码后，可在其他设备恢复相同情况。请妥善保管代码。',
    cloudSave: '生成保存码',
    cloudRestore: '用代码恢复',
    cloudSaved: '保存码：{code}',
    cloudSaveFailed: '云端保存失败',
  },
  focus: {
    title: '本周重点',
    noProfile: '设置预产期后，可按当前阶段引导',
    setDates: '设置日期',
    editDates: '修改日期',
    allDone: '本阶段清单已全部完成。辛苦了。',
  },
  weekCard: {
    title: '孕 {week} 周 {days} 天',
    sizeLabel: '大小参考',
    fruitLike: '大约像 {fruit}',
    disclaimer: '大小仅为一般参考，存在个体差异。',
    postpartumTitle: '产后此刻',
    postpartumTip: '恢复与育儿因人而异。能睡就睡，能求助就求助。',
  },
  search: {
    ariaLabel: '搜索清单项目',
    placeholder: '搜索手续或购物',
    empty: '没有匹配项目',
    done: '已完成',
  },
  share: {
    title: '与家人同步进度',
    intro:
      '面向在新潟市生活的孕产家庭。妈妈生成共享码，发给丈夫、家人或朋友后，即可看到相同清单进度。',
    forMomTitle: '① 妈妈：生成共享码',
    forMomBody: '把当前清单和预产期保存到云端并生成短码。同一个码可以反复更新。',
    createCode: '生成共享码',
    updateCode: '用最新进度更新此码',
    codeLabel: '共享码',
    copyCode: '复制代码',
    codeCopied: '已复制。请用 LINE 等发送给家人。',
    sendHint: '请把这个代码发给家人。',
    forFamilyTitle: '② 丈夫 / 家人：用代码同步',
    forFamilyBody: '输入收到的代码并点「同步」，清单进度和预产期会导入到本设备。',
    codePlaceholder: '例如：ABCD-EFGH',
    syncButton: '同步',
    syncOk: '已同步家人的进度',
    syncFailed: '请检查代码后重试',
    copyText: '复制进度文本',
    copiedText: '已复制文本',
    copyFailed: '复制失败，请手动选择。',
    textHint: '如果只想发送文字摘要，也可以用这个。',
    autoOn: '已开启自动同步',
    autoHint:
      '连接一次后，打开应用、勾选变化、大约每3分钟会自动同步。不必每次重新生成代码。',
    unlink: '关闭自动同步',
    unlinked: '已关闭自动同步',
    lastSync: '上次同步：{time}',
  },
};

export const uiDictionary: Record<Locale, UiDictionary> = {
  ja,
  en,
  zh,
};

export function getUi(locale: Locale): UiDictionary {
  return uiDictionary[locale] ?? ja;
}

export function t(
  locale: Locale,
  path: keyof UiDictionary,
  key: string
): string {
  const dict = getUi(locale);
  const section = dict[path] as Record<string, unknown>;
  const value = section?.[key];
  return typeof value === 'string' ? value : key;
}
